import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import QuoteModal from './components/QuoteModal';
import { useWebsiteData } from '../../context/WebsiteDataContext';

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { getActiveProjects } = useWebsiteData();
  const projects = getActiveProjects();

  const [project, setProject] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [relatedProjects, setRelatedProjects] = useState([]);

  useEffect(() => {
    // Compare as strings since IDs are now strings from Firestore
    const foundProject = projects.find(p => String(p.id) === String(projectId));
    if (foundProject) {
      setProject(foundProject);
      // Get related projects (same category, excluding current project)
      const related = projects
        .filter(p => String(p.id) !== String(projectId) && p.category === foundProject.category)
        .slice(0, 3);
      setRelatedProjects(related);
    } else {
      // Project not found, redirect to projects page
      navigate('/projects');
    }
  }, [projectId, projects, navigate]);

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'residential': return 'Home';
      case 'commercial': return 'Building';
      case 'industrial': return 'Factory';
      case 'interior': return 'Palette';
      default: return 'Building';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in-progress': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleGetQuote = () => {
    setShowQuoteModal(true);
  };

  const handleQuoteModalClose = () => {
    setShowQuoteModal(false);
  };

  if (!project) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Icon name="Loader" size={48} className="mx-auto text-primary animate-spin mb-4" />
            <p className="text-muted-foreground">Loading project details...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const allImages = [project.image, ...(project.gallery || [])].filter(Boolean);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Breadcrumb */}
      <section className="pt-24 pb-4 bg-card border-b border-border">
        <div className="container mx-auto px-4">
          <nav className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <Icon name="ChevronRight" size={14} />
            <Link to="/projects" className="hover:text-foreground transition-colors">Projects</Link>
            <Icon name="ChevronRight" size={14} />
            <span className="text-foreground font-medium">{project.title}</span>
          </nav>
        </div>
      </section>

      {/* Project Header */}
      <section className="py-8 bg-card">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Project Info */}
            <div className="lg:col-span-2">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                    {project.title}
                  </h1>
                  <p className="text-lg text-muted-foreground mb-4">
                    {project.description}
                  </p>
                </div>
                <div className="flex space-x-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(project.status)}`}>
                    {project.status === 'completed' ? 'Completed' : 'In Progress'}
                  </span>
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary border border-primary/20">
                    {project.category.charAt(0).toUpperCase() + project.category.slice(1)}
                  </span>
                </div>
              </div>

              {/* Key Details */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-muted/30 p-4 rounded-lg">
                  <div className="flex items-center space-x-2 mb-1">
                    <Icon name="MapPin" size={16} className="text-primary" />
                    <span className="text-sm text-muted-foreground">Location</span>
                  </div>
                  <p className="font-medium">{project.location}</p>
                </div>
                <div className="bg-muted/30 p-4 rounded-lg">
                  <div className="flex items-center space-x-2 mb-1">
                    <Icon name="Ruler" size={16} className="text-primary" />
                    <span className="text-sm text-muted-foreground">Area</span>
                  </div>
                  <p className="font-medium">{project.area}</p>
                </div>
                <div className="bg-muted/30 p-4 rounded-lg">
                  <div className="flex items-center space-x-2 mb-1">
                    <Icon name="Calendar" size={16} className="text-primary" />
                    <span className="text-sm text-muted-foreground">Duration</span>
                  </div>
                  <p className="font-medium">{project.duration}</p>
                </div>
                <div className="bg-muted/30 p-4 rounded-lg">
                  <div className="flex items-center space-x-2 mb-1">
                    <Icon name="DollarSign" size={16} className="text-primary" />
                    <span className="text-sm text-muted-foreground">Budget</span>
                  </div>
                  <p className="font-medium">{project.budget}</p>
                </div>
              </div>

              {/* Services */}
              {project.services && project.services.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-foreground mb-3">Services Provided</h3>
                  <div className="flex flex-wrap gap-2">
                    {project.services.map((service, index) => (
                      <span key={index} className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full border border-primary/20">
                        {service}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* CTA Card */}
            <div className="lg:col-span-1">
              <div className="bg-card border border-border rounded-lg p-6 sticky top-24">
                <h3 className="text-lg font-semibold text-foreground mb-4">Interested in Similar Project?</h3>
                <p className="text-muted-foreground mb-6">
                  Get a free consultation and quote for your project requirements.
                </p>
                <div className="space-y-3">
                  <Button
                    variant="default"
                    size="lg"
                    fullWidth
                    iconName="MessageSquare"
                    iconPosition="left"
                    onClick={handleGetQuote}
                  >
                    Get Quote
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    fullWidth
                    iconName="Phone"
                    iconPosition="left"
                  >
                    Call Now
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    fullWidth
                    iconName="Whatsapp"
                    iconPosition="left"
                  >
                    WhatsApp
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Gallery */}
      {allImages.length > 0 && (
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-foreground mb-8 text-center">Project Gallery</h2>

            {/* Main Image */}
            <div className="mb-6">
              <div className="relative aspect-video rounded-lg overflow-hidden bg-black flex items-center justify-center">
                <Image
                  src={allImages[activeImageIndex]}
                  alt={`${project.title} - Image ${activeImageIndex + 1}`}
                  className="w-full h-full object-contain"
                />
                {allImages.length > 1 && (
                  <>
                    <button
                      onClick={() => setActiveImageIndex(prev => prev === 0 ? allImages.length - 1 : prev - 1)}
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
                    >
                      <Icon name="ChevronLeft" size={20} />
                    </button>
                    <button
                      onClick={() => setActiveImageIndex(prev => prev === allImages.length - 1 ? 0 : prev + 1)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
                    >
                      <Icon name="ChevronRight" size={20} />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Thumbnail Gallery */}
            {allImages.length > 1 && (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {allImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveImageIndex(index)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${activeImageIndex === index
                      ? 'border-primary'
                      : 'border-border hover:border-primary/50'
                      }`}
                  >
                    <Image
                      src={image}
                      alt={`${project.title} - Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Project Details */}
      <section className="py-12 bg-card">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Project Highlights */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-6">Project Highlights</h2>
              <div className="space-y-4">
                {project.highlights && project.highlights.map((highlight, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Icon name="CheckCircle" size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                    <p className="text-muted-foreground">{highlight}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Project Information */}
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-6">Project Information</h2>
              <div className="space-y-4">
                <div className="flex justify-between py-3 border-b border-border">
                  <span className="text-muted-foreground">Client</span>
                  <span className="font-medium">{project.client}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-border">
                  <span className="text-muted-foreground">Completion Date</span>
                  <span className="font-medium">{formatDate(project.completionDate)}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-border">
                  <span className="text-muted-foreground">Project Rating</span>
                  <div className="flex items-center space-x-1">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Icon
                          key={i}
                          name={i < project.rating ? "Star" : "Star"}
                          size={16}
                          className={i < project.rating ? "text-yellow-500 fill-current" : "text-gray-300"}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-muted-foreground">({project.rating}/5)</span>
                  </div>
                </div>
                <div className="flex justify-between py-3 border-b border-border">
                  <span className="text-muted-foreground">Category</span>
                  <span className="font-medium capitalize">{project.category}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-border">
                  <span className="text-muted-foreground">Status</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                    {project.status === 'completed' ? 'Completed' : 'In Progress'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Testimonial */}
      {project.testimonial && (
        <section className="py-12 bg-muted/20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Icon name="Quote" size={48} className="mx-auto text-primary mb-6" />
              <blockquote className="text-xl md:text-2xl text-foreground mb-6 italic">
                "{project.testimonial}"
              </blockquote>
              <div className="flex items-center justify-center space-x-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="User" size={24} className="text-primary" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-foreground">{project.client}</p>
                  <p className="text-sm text-muted-foreground">Client</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Related Projects */}
      {relatedProjects.length > 0 && (
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-foreground mb-8 text-center">Related Projects</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedProjects.map((relatedProject) => (
                <div key={relatedProject.id} className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="relative aspect-video">
                    <Image
                      src={relatedProject.image}
                      alt={relatedProject.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4 flex space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(relatedProject.status)}`}>
                        {relatedProject.status === 'completed' ? 'Completed' : 'In Progress'}
                      </span>
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                        {relatedProject.category.charAt(0).toUpperCase() + relatedProject.category.slice(1)}
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-foreground mb-2 line-clamp-2">{relatedProject.title}</h3>
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{relatedProject.description}</p>
                    <div className="flex items-center justify-between text-sm text-muted-foreground mb-3">
                      <span>{relatedProject.location}</span>
                      <span>{relatedProject.budget}</span>
                    </div>
                    <Link to={`/project/${relatedProject.id}`}>
                      <Button variant="outline" size="sm" fullWidth>
                        View Details
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Start Your Project?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto opacity-90">
            Let's discuss your vision and create something amazing together. Our team is ready to bring your ideas to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="secondary"
              size="lg"
              iconName="Calendar"
              iconPosition="left"
              onClick={() => window.dispatchEvent(new Event('open-consultation-modal'))}
            >
              Book Free Consultation
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="MessageCircle"
              iconPosition="left"
              className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
            >
              Chat on WhatsApp
            </Button>
          </div>
        </div>
      </section>

      <QuoteModal
        isOpen={showQuoteModal}
        onClose={handleQuoteModalClose}
        projectTitle={project.title}
      />

      <Footer />
    </div>
  );
};

export default ProjectDetail; 